import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { securityObjectivesApi, peopleApi } from '../services/api';
import { LinkedArtefacts } from '../components/LinkedArtefacts';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ProgressEntry = {
  date: string;
  value: number;
  note?: string;
  recordedAt?: string;
};

type SecurityObjective = {
  objectiveId: string;
  title: string;
  description?: string;
  category?: string;
  ownerId?: string;
  ownerName?: string;
  priority?: string;
  targetDate?: string;
  progress?: number;
  status?: string;
  howMeasured?: string;
  resourcesRequired?: string;
  notes?: string;
  trackingMode?: string;
  metricLabel?: string;
  metricTarget?: number;
  metricCurrent?: number;
  metricUnit?: string;
  progressHistory?: ProgressEntry[];
};

const EMPTY_FORM: Omit<SecurityObjective, 'objectiveId'> = {
  title: '',
  description: '',
  category: '',
  ownerId: '',
  ownerName: '',
  priority: 'Medium',
  targetDate: '',
  progress: 0,
  status: 'Not Started',
  howMeasured: '',
  resourcesRequired: '',
  notes: '',
  trackingMode: 'progress',
  metricLabel: '',
  metricTarget: 0,
  metricCurrent: 0,
  metricUnit: '',
  progressHistory: [],
};

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

const CATEGORIES = [
  'Access Control',
  'Asset Management',
  'Awareness & Training',
  'Business Continuity',
  'Compliance',
  'Cryptography',
  'Human Resources',
  'Incident Response',
  'Network Security',
  'Physical Security',
  'Risk Management',
  'Supplier & Third Party',
  'Vulnerability Management',
  'Other',
];

const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

const STATUSES = [
  'Not Started',
  'In Progress',
  'On Track',
  'At Risk',
  'Completed',
  'Cancelled',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getProgressColour = (progress: number, status?: string, metricMode = false) => {
  if (status === 'Completed') return 'np-tone-success';
  if (status === 'At Risk')   return 'np-tone-danger';
  if (metricMode) return progress >= 100 ? 'np-tone-success' : 'np-tone-danger';
  if (progress >= 75) return 'np-tone-success';
  if (progress >= 40) return 'np-tone-info';
  return 'np-tone-warning';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SecurityObjectiveFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const isEdit = Boolean(id);
  const [activeTab, setActiveTab] = useState<'details' | 'links'>('details');
  const [form, setForm] = useState<Omit<SecurityObjective, 'objectiveId'>>(EMPTY_FORM);
  const [loaded, setLoaded] = useState(!isEdit);

  // Owner picker
  const [ownerSearch, setOwnerSearch] = useState('');
  const [showOwnerDrop, setShowOwnerDrop] = useState(false);
  const ownerInputRef = useRef<HTMLInputElement>(null);

  // Load existing record when editing
  const { data: existing, isLoading: loadingExisting } = useQuery<SecurityObjective>({
    queryKey: ['security-objective', id],
    queryFn: () => securityObjectivesApi.getOne(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing) {
      setForm({
        title: existing.title || '',
        description: existing.description || '',
        category: existing.category || '',
        ownerId: existing.ownerId || '',
        ownerName: existing.ownerName || '',
        priority: existing.priority || 'Medium',
        targetDate: existing.targetDate || '',
        progress: existing.progress ?? 0,
        status: existing.status || 'Not Started',
        howMeasured: existing.howMeasured || '',
        resourcesRequired: existing.resourcesRequired || '',
        notes: existing.notes || '',
        trackingMode: existing.trackingMode || 'progress',
        metricLabel: existing.metricLabel || '',
        metricTarget: existing.metricTarget ?? 0,
        metricCurrent: existing.metricCurrent ?? 0,
        metricUnit: existing.metricUnit || '',
        progressHistory: existing.progressHistory || [],
      });
      setOwnerSearch(existing.ownerName || '');
      setLoaded(true);
    }
  }, [existing]);

  const { data: people = [] } = useQuery<any[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const setField = (key: keyof typeof EMPTY_FORM, value: any) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleStatusChange = (newStatus: string) => {
    setForm((f) => {
      if (f.trackingMode === 'metric') return { ...f, status: newStatus };
      let progress = f.progress ?? 0;
      if (newStatus === 'Completed') progress = 100;
      else if (newStatus === 'Not Started') progress = 0;
      else if (newStatus === 'Cancelled') progress = progress;
      else if (progress === 0 || progress === 100) {
        if (newStatus === 'In Progress') progress = 25;
        else if (newStatus === 'On Track') progress = 50;
        else if (newStatus === 'At Risk') progress = 25;
      }
      return { ...f, status: newStatus, progress };
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const saveData = { ...form };
      if (form.trackingMode === 'metric' && form.metricTarget && form.metricTarget > 0) {
        saveData.progress = Math.min(100, Math.max(0, Math.round(((form.metricCurrent ?? 0) / form.metricTarget) * 100)));
      }
      if (isEdit) {
        await securityObjectivesApi.update(id!, saveData);
      } else {
        await securityObjectivesApi.create(saveData);
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['security-objectives'] });
      if (isEdit) {
        await qc.invalidateQueries({ queryKey: ['security-objective', id] });
        toast.success('Objective updated.');
      } else {
        toast.success('Objective created.');
        navigate('/security-objectives');
      }
    },
    onError: (e: Error) => toast.error(`Failed to save: ${e.message}`),
  });

  const getToday = () => new Date().toISOString().split('T')[0];
  const [logEntry, setLogEntry] = useState({ date: getToday(), value: '', note: '' });

  const logProgressMutation = useMutation({
    mutationFn: async () => {
      const entry: ProgressEntry = {
        date: logEntry.date,
        value: Number(logEntry.value),
        note: logEntry.note || undefined,
        recordedAt: new Date().toISOString(),
      };
      const newHistory = [...(form.progressHistory || []), entry];
      const updates = {
        ...form,
        progressHistory: newHistory,
        ...(form.trackingMode === 'metric'
          ? { metricCurrent: Number(logEntry.value) }
          : { progress: Math.min(100, Math.max(0, Number(logEntry.value))) }),
      };
      return securityObjectivesApi.update(id!, updates);
    },
    onSuccess: (data: any) => {
      setForm((f) => ({
        ...f,
        progressHistory: data.progressHistory || [],
        ...(form.trackingMode === 'metric'
          ? { metricCurrent: Number(logEntry.value) }
          : { progress: Math.min(100, Math.max(0, Number(logEntry.value))) }),
      }));
      setLogEntry({ date: getToday(), value: '', note: '' });
      qc.invalidateQueries({ queryKey: ['security-objective', id] });
      toast.success('Progress logged.');
    },
    onError: (e: Error) => toast.error(`Failed to log progress: ${e.message}`),
  });

  // Owner picker filtered list
  const filteredPeople = useMemo(() => {
    const q = ownerSearch.toLowerCase();
    if (!q) return people.slice(0, 8);
    return people
      .filter((p) => {
        const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim().toLowerCase();
        return fullName.includes(q) || (p.jobTitle || '').toLowerCase().includes(q);
      })
      .slice(0, 8);
  }, [people, ownerSearch]);

  // Derived progress for live bar
  const derivedProgress = form.trackingMode === 'metric'
    ? (form.metricTarget && form.metricTarget > 0
      ? Math.min(100, Math.max(0, Math.round(((form.metricCurrent ?? 0) / form.metricTarget) * 100)))
      : 0)
    : (form.progress ?? 0);

  // ---------------------------------------------------------------- render

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-shield-star-line"
        title={isEdit ? 'Edit Security Objective' : 'New Security Objective'}
        colour="primary"
        actions={
          <button
            className="np-btn np-btn-ghost"
            onClick={() => navigate('/security-objectives')}
          >
            <i className="ri-arrow-left-line"></i>Back to Objectives
          </button>
        }
      />

      {isEdit && loadingExisting && !loaded ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
          <div className="np-sub mt-2 small">Loading…</div>
        </div>
      ) : (
        <div>
          {/* ── Tab navigation ── */}
          <div className="np-tabs mb-4">
            <button
              type="button"
              className={`np-tab d-flex align-items-center gap-1${activeTab === 'details' ? ' active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              <i className="ri-file-list-3-line"></i>Details
            </button>
            <button
              type="button"
              className={`np-tab d-flex align-items-center gap-1${activeTab === 'links' ? ' active' : ''}`}
              onClick={() => setActiveTab('links')}
            >
              <i className="ri-link"></i>Linked Artefacts
            </button>
          </div>

          {/* ── Linked Artefacts tab ── */}
          {activeTab === 'links' && (
            isEdit ? (
              <LinkedArtefacts
                artefactId={id!}
                artefactType="security_objective"
                artefactRegister="security_objective_register"
              />
            ) : (
              <div className="np-card mb-4">
                <div className="np-card-body">
                  <div className="text-center py-5 np-sub">
                    <i className="ri-link ri-3x d-block mb-3 opacity-25"></i>
                    <p className="mb-1 fw-semibold">Linked Artefacts not yet available</p>
                    <p className="small mb-0">Save this objective first, then come back to link risks, controls, evidence and other artefacts.</p>
                  </div>
                </div>
              </div>
            )
          )}

          {/* ── Details tab ── */}
          {activeTab === 'details' && (
            <>
              {/* ── Section 1: The Objective ── */}
              <div className="np-card mb-4">
                <div className="np-card-head">
                  <h2 className="d-flex align-items-center gap-2">
                    <span className="np-chip np-tone-primary">1</span>
                    The Objective
                  </h2>
                </div>
                <div className="np-card-body">

                  <div className="np-field">
                    <label>
                      Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="np-input"
                      placeholder="e.g. Reduce unauthorised access incidents by 50%"
                      value={form.title}
                      onChange={(e) => setField('title', e.target.value)}
                    />
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6 np-field">
                      <label>Description</label>
                      <textarea
                        className="np-textarea"
                        rows={3}
                        placeholder="What does success look like? What will be done?"
                        value={form.description}
                        onChange={(e) => setField('description', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 np-field">
                      <label>How Results Will Be Evaluated</label>
                      <textarea
                        className="np-textarea"
                        rows={3}
                        placeholder="e.g. Monthly vulnerability scan reports compared against baseline"
                        value={form.howMeasured}
                        onChange={(e) => setField('howMeasured', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Section 2: Assignment & Timeline ── */}
              <div className="np-card mb-4">
                <div className="np-card-head">
                  <h2 className="d-flex align-items-center gap-2">
                    <span className="np-chip np-tone-primary">2</span>
                    Assignment &amp; Timeline
                  </h2>
                </div>
                <div className="np-card-body">

                  <div className="row g-3 mb-3">
                    {/* Owner picker */}
                    <div className="col-md-6 np-field position-relative">
                      <label>Owner</label>
                      <input
                        ref={ownerInputRef}
                        type="text"
                        className="np-input"
                        placeholder="Search for a person…"
                        value={ownerSearch}
                        autoComplete="off"
                        onChange={(e) => {
                          setOwnerSearch(e.target.value);
                          setShowOwnerDrop(true);
                          if (!e.target.value) { setField('ownerId', ''); setField('ownerName', ''); }
                        }}
                        onFocus={() => setShowOwnerDrop(true)}
                        onBlur={() => setTimeout(() => setShowOwnerDrop(false), 180)}
                      />
                      {showOwnerDrop && filteredPeople.length > 0 && (
                        <div
                          className="np-popover"
                          style={{ right: 0, maxHeight: 220, overflowY: 'auto' }}
                        >
                          {filteredPeople.map((p) => {
                            const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim();
                            const initials = `${(p.firstName || ' ')[0]}${(p.lastName || ' ')[0]}`.toUpperCase();
                            const subtitle = [p.jobTitle, p.departmentName || p.department].filter(Boolean).join(' · ');
                            return (
                              <div
                                key={p.personId}
                                className="np-popover-item"
                                onMouseDown={() => {
                                  setField('ownerId', p.personId);
                                  setField('ownerName', fullName);
                                  setOwnerSearch(fullName);
                                  setShowOwnerDrop(false);
                                }}
                              >
                                <div className="avatar avatar-xs flex-shrink-0">
                                  <span className="avatar-initial rounded-circle bg-label-primary" style={{ fontSize: '0.65rem' }}>
                                    {initials}
                                  </span>
                                </div>
                                <div>
                                  <div className="fw-semibold small">{fullName}</div>
                                  {subtitle && <div className="np-hint mb-0">{subtitle}</div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="col-md-2 np-field">
                      <label>Category</label>
                      <select
                        className="np-select"
                        value={form.category}
                        onChange={(e) => setField('category', e.target.value)}
                      >
                        <option value="">Select…</option>
                        {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="col-md-2 np-field">
                      <label>Priority</label>
                      <select
                        className="np-select"
                        value={form.priority}
                        onChange={(e) => setField('priority', e.target.value)}
                      >
                        {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                    <div className="col-md-2 np-field">
                      <label>Target Date</label>
                      <input
                        type="date"
                        className="np-input"
                        value={form.targetDate}
                        onChange={(e) => setField('targetDate', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="np-field np-mb-0">
                    <label>Resources Required</label>
                    <textarea
                      className="np-textarea"
                      rows={2}
                      placeholder="e.g. Security team, vulnerability scanner licence, Q1 budget"
                      value={form.resourcesRequired}
                      onChange={(e) => setField('resourcesRequired', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* ── Section 3: Progress & Tracking ── */}
              <div className="np-card mb-4">
                <div className="np-card-head">
                  <h2 className="d-flex align-items-center gap-2">
                    <span className="np-chip np-tone-primary">3</span>
                    Progress &amp; Tracking
                  </h2>
                </div>
                <div className="np-card-body">

                  <div className="row g-3 mb-3">
                    <div className="col-md-4 np-field">
                      <label>Status</label>
                      <select
                        className="np-select"
                        value={form.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                      >
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4 np-field">
                      <label>Tracking Method</label>
                      <select
                        className="np-select"
                        value={form.trackingMode || 'progress'}
                        onChange={(e) => {
                          const mode = e.target.value;
                          setForm((f) => ({
                            ...f,
                            trackingMode: mode,
                            ...(mode === 'progress' ? { metricLabel: '', metricTarget: 0, metricCurrent: 0, metricUnit: '' } : {}),
                          }));
                        }}
                      >
                        <option value="progress">Progress Bar (% complete)</option>
                        <option value="metric">Metric (measure against a target)</option>
                      </select>
                    </div>
                    {(form.trackingMode || 'progress') === 'progress' && (
                      <div className="col-md-4 np-field">
                        <label>% Complete</label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="np-input"
                            min={0}
                            max={100}
                            step={5}
                            value={form.progress ?? 0}
                            onChange={(e) => {
                              const v = Math.min(100, Math.max(0, Number(e.target.value)));
                              setField('progress', v);
                            }}
                            disabled={form.status === 'Completed' || form.status === 'Not Started'}
                          />
                          <span className="input-group-text">%</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Metric mode fields */}
                  {form.trackingMode === 'metric' && (
                    <div className="row g-3 mb-3">
                      <div className="col-md-4 np-field">
                        <label>Metric Label</label>
                        <input
                          type="text"
                          className="np-input"
                          placeholder="e.g. Open vulnerabilities"
                          value={form.metricLabel || ''}
                          onChange={(e) => setField('metricLabel', e.target.value)}
                        />
                      </div>
                      <div className="col-md-2 np-field">
                        <label>Unit</label>
                        <input
                          type="text"
                          className="np-input"
                          placeholder="e.g. issues"
                          value={form.metricUnit || ''}
                          onChange={(e) => setField('metricUnit', e.target.value)}
                        />
                      </div>
                      <div className="col-md-3 np-field">
                        <label>Target Value</label>
                        <input
                          type="number"
                          className="np-input"
                          min={0}
                          placeholder="10"
                          value={form.metricTarget || ''}
                          onChange={(e) => setField('metricTarget', e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                      </div>
                      <div className="col-md-3 np-field">
                        <label>Current Value</label>
                        <input
                          type="number"
                          className="np-input"
                          min={0}
                          placeholder="40"
                          value={form.metricCurrent || ''}
                          onChange={(e) => setField('metricCurrent', e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                      </div>
                    </div>
                  )}

                  {/* Live progress bar */}
                  <div className="mb-3">
                    {form.trackingMode === 'metric' && form.metricTarget ? (
                      <div className="d-flex justify-content-between small np-hint mb-1">
                        <span>
                          {form.metricCurrent ?? 0}{form.metricUnit ? ` ${form.metricUnit}` : ''} of {form.metricTarget}{form.metricUnit ? ` ${form.metricUnit}` : ''}
                          {form.metricLabel ? ` ${form.metricLabel}` : ''}
                        </span>
                        <span className="fw-semibold np-tabular">{derivedProgress}%</span>
                      </div>
                    ) : null}
                    <div className="np-progress" style={{ height: 8 }}>
                      <div
                        className={`np-fill ${getProgressColour(derivedProgress, form.status, form.trackingMode === 'metric')}`}
                        style={{ width: `${derivedProgress}%`, transition: 'width 0.2s' }}
                      ></div>
                    </div>
                    <div className="np-hint mt-1">
                      {form.status === 'Completed' && '✓ Marked complete'}
                      {form.status === 'Not Started' && 'Progress locked at 0% until work begins'}
                      {(form.status === 'In Progress' || form.status === 'On Track' || form.status === 'At Risk') &&
                        (form.trackingMode === 'metric' ? 'Progress derived from current / target values' : 'Update % to reflect current progress')}
                    </div>
                  </div>

                  <div className="np-field">
                    <label>Notes / Updates</label>
                    <textarea
                      className="np-textarea"
                      rows={3}
                      placeholder="Progress notes, blockers, decisions…"
                      value={form.notes}
                      onChange={(e) => setField('notes', e.target.value)}
                    />
                  </div>

                  {/* ── Progress Log ── */}
                  {isEdit && (
                    <div className="mt-4 pt-3 border-top">
                      <h6 className="fw-semibold mb-3">
                        <i className="ri-history-line me-1"></i>Log Progress Entry
                      </h6>
                      <div className="row g-3 align-items-end mb-3">
                        <div className="col-md-3 np-field np-mb-0">
                          <label>Date</label>
                          <input
                            type="date"
                            className="np-input"
                            value={logEntry.date}
                            onChange={(e) => setLogEntry((l) => ({ ...l, date: e.target.value }))}
                          />
                        </div>
                        <div className="col-md-3 np-field np-mb-0">
                          <label>
                            {form.trackingMode === 'metric'
                              ? `Value${form.metricUnit ? ` (${form.metricUnit})` : ''}`
                              : '% Complete'}
                          </label>
                          <input
                            type="number"
                            className="np-input"
                            min={0}
                            max={form.trackingMode !== 'metric' ? 100 : undefined}
                            placeholder={form.trackingMode !== 'metric' ? '0–100' : (form.metricLabel || 'value')}
                            value={logEntry.value}
                            onChange={(e) => setLogEntry((l) => ({ ...l, value: e.target.value }))}
                          />
                        </div>
                        <div className="col-md-4 np-field np-mb-0">
                          <label>
                            Note <span className="np-hint fw-normal">(optional)</span>
                          </label>
                          <input
                            type="text"
                            className="np-input"
                            placeholder="e.g. Completed Q1 training run"
                            value={logEntry.note}
                            onChange={(e) => setLogEntry((l) => ({ ...l, note: e.target.value }))}
                          />
                        </div>
                        <div className="col-md-2">
                          <button
                            className="np-btn np-btn-primary w-100"
                            onClick={() => logProgressMutation.mutate()}
                            disabled={!logEntry.value || logProgressMutation.isPending}
                          >
                            {logProgressMutation.isPending
                              ? <span className="spinner-border spinner-border-sm" />
                              : <><i className="ri-add-line me-1" />Log</>}
                          </button>
                        </div>
                      </div>

                      {(form.progressHistory || []).length > 0 && (() => {
                        const sorted = [...(form.progressHistory as ProgressEntry[])].sort((a, b) => a.date.localeCompare(b.date));
                        const displayed = [...sorted].reverse();
                        const suffix = form.trackingMode === 'metric' ? (form.metricUnit ? ` ${form.metricUnit}` : '') : '%';
                        return (
                          <div className="np-table-wrap mt-1">
                            <table className="np-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>{form.trackingMode === 'metric' ? `Value${form.metricUnit ? ` (${form.metricUnit})` : ''}` : 'Progress'}</th>
                                  <th>Change</th>
                                  <th>Note</th>
                                </tr>
                              </thead>
                              <tbody>
                                {displayed.map((entry, i) => {
                                  const chronoIdx = sorted.length - 1 - i;
                                  const prev = chronoIdx > 0 ? sorted[chronoIdx - 1] : null;
                                  const delta = prev !== null ? entry.value - prev.value : null;
                                  return (
                                    <tr key={i}>
                                      <td className="np-tabular">{entry.date}</td>
                                      <td className="fw-semibold np-tabular">{entry.value}{suffix}</td>
                                      <td>
                                        {delta !== null ? (
                                          <span className={`np-tabular ${delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'np-hint'}`}>
                                            {delta > 0 ? '+' : ''}{delta}{suffix}
                                          </span>
                                        ) : (
                                          <span className="np-hint">—</span>
                                        )}
                                      </td>
                                      <td className="np-hint">{entry.note || '—'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Save / Cancel */}
              <div className="d-flex justify-content-end gap-2 mb-4">
                <button
                  className="np-btn np-btn-ghost"
                  onClick={() => navigate('/security-objectives')}
                >
                  Cancel
                </button>
                <button
                  className="np-btn np-btn-primary"
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending || !form.title.trim()}
                >
                  {saveMutation.isPending
                    ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</>
                    : isEdit ? 'Save Changes' : 'Create Objective'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
